import React, { useEffect, useMemo, useState } from "react";
import {
    fetchAdmin1ByCountry,
    fetchAdmin2ByAdmin1,
    fetchCitiesByAdmin2,
    fetchCountries,
    fetchPostalCodesByCity,
    reverseGeocode,
    ReverseGeocode,
} from "../../api/GeoDataAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { CoverageArea } from "../../utils/coverage";
import { deepestName, getBrowserPosition, loadLastLocation, saveLastLocation } from "../../utils/lastLocation";

type IdName = { id: string | number; name: string };
type Postal = { id: string | number; postalCode: string; placeName?: string };

interface Props {
    token: string;
    /** Emits the selected chain (Country → deepest), each {level, id, name}. */
    onChange: (chain: CoverageArea[]) => void;
    /** Shows a hint explaining what the selection means:
     *  "provider" = you serve this area; "searcher" = you need service here;
     *  "item" = the listed thing is located here; "itemSearcher" = you are
     *  looking for listings located in this region. */
    context?: "provider" | "searcher" | "item" | "itemSearcher";
    /** Pre-fills the picker from geo ids (applied once on mount; names are
     *  resolved from each level's list). Used by the personalized why page
     *  to open the demo search already pointed at the prospect's own place. */
    initial?: {
        countryId?: number | null;
        admin1Id?: number | null;
        admin2Id?: number | null;
        cityId?: number | null;
    } | null;
}

export const LEVEL_LABEL: Record<string, string> = {
    Country: "the whole country",
    Admin1: "the whole state/region",
    Admin2: "the whole county",
    City: "the city",
    PostalCode: "this postal code area",
};

// What the user can drill down to from each level.
export const NEXT_LEVEL: Record<string, string> = {
    Country: "a state/region",
    Admin1: "a county/district",
    Admin2: "a city",
    City: "a postal code",
};

function hintFor(context: "provider" | "searcher" | "item" | "itemSearcher", deepest: CoverageArea | null): string {
    if (!deepest) {
        switch (context) {
            case "provider":
                return "Choose the area you can serve. Picking a country means you serve the whole country; pick a state or city to narrow it.";
            case "item":
                return "Set where this listing is located — go down to at least the state, ideally the city.";
            case "itemSearcher":
                return "Set where you are looking. Picking a country searches the whole country; you can then narrow down to a state, county, or city.";
            default:
                return "Set where you need the service. Picking a country searches the whole country; you can then narrow down to a state, county, or city.";
        }
    }
    const scope = LEVEL_LABEL[deepest.level] ?? deepest.level;
    const next = NEXT_LEVEL[deepest.level];
    switch (context) {
        case "provider":
            return `You are saying you can provide this service anywhere in ${deepest.name} (${scope}).`;
        case "item": {
            // A coarse location excludes the listing from finer searches:
            // located-in-California matches state/country searches but not a
            // search for a specific county or city. Spell that out per level.
            const finerWarning: Record<string, string> = {
                Country: "searches for a specific state, county, or city will NOT include it",
                Admin1: "it will appear in state-wide and country-wide searches, but searches for a specific county or city will NOT include it",
                Admin2: "it will appear in county, state, and country searches, but searches for a specific city will NOT include it",
            };
            const warning = finerWarning[deepest.level];
            return `You are saying this listing is located in ${deepest.name} (${scope}).`
                + (warning
                    ? ` Note: ${warning} — go down to the city if you can.`
                    : ` It will appear in searches for ${deepest.name} and any wider area (county, state, country).`);
        }
        case "itemSearcher":
            return `This will search for listings located in ${deepest.name} (${scope}).`
                + (next ? ` You can further narrow the search to ${next}.` : "");
        default:
            return `This will search for service providers that can provide service in ${deepest.name} (${scope}).`
                + (next ? ` You can further narrow the search to ${next}.` : "");
    }
}

/** Cascading country → admin1 → admin2 → city → postal picker that reports the
 *  whole selected chain (used for coverage: the deepest node = an area to cover;
 *  the full chain = the searcher's coverageChain). */
export default function GeoAreaPicker({ token, onChange, context, initial }: Props) {
    const [countries, setCountries] = useState<IdName[]>([]);
    const [admin1List, setAdmin1List] = useState<IdName[]>([]);
    const [admin2List, setAdmin2List] = useState<IdName[]>([]);
    const [cities, setCities] = useState<IdName[]>([]);
    const [postalCodes, setPostalCodes] = useState<Postal[]>([]);

    const [country, setCountry] = useState<CoverageArea | null>(null);
    const [admin1, setAdmin1] = useState<CoverageArea | null>(null);
    const [admin2, setAdmin2] = useState<CoverageArea | null>(null);
    const [city, setCity] = useState<CoverageArea | null>(null);
    const [postal, setPostal] = useState<CoverageArea | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [locating, setLocating] = useState(false);
    const [lastLoc] = useState<ReverseGeocode | null>(loadLastLocation);

    const chain = useMemo<CoverageArea[]>(
        () => [country, admin1, admin2, city, postal].filter((x): x is CoverageArea => !!x),
        [country, admin1, admin2, city, postal]
    );

    useEffect(() => { onChange(chain); }, [chain, onChange]);

    useEffect(() => {
        (async () => {
            try { setError(null); setCountries((await fetchCountries(token)) ?? []); }
            catch (e: any) { logApiError(e); setError(getUserMessage(e)); }
        })();
    }, [token]);

    const nameOf = (list: IdName[], id: string) =>
        list.find((x) => String(x.id) === id)?.name ?? id;

    const load = async (fn: () => Promise<any>, set: (v: any[]) => void) => {
        try { set((await fn()) ?? []); }
        catch (e: any) { logApiError(e); setError(getUserMessage(e)); }
    };

    const onCountry = async (v: string) => {
        setAdmin1(null); setAdmin2(null); setCity(null); setPostal(null);
        setAdmin1List([]); setAdmin2List([]); setCities([]); setPostalCodes([]);
        setError(null);
        if (!v) { setCountry(null); return; }
        setCountry({ level: "Country", id: Number(v), name: nameOf(countries, v) });
        await load(() => fetchAdmin1ByCountry(token, v), setAdmin1List);
    };
    const onAdmin1 = async (v: string) => {
        setAdmin2(null); setCity(null); setPostal(null);
        setAdmin2List([]); setCities([]); setPostalCodes([]);
        setError(null);
        if (!v) { setAdmin1(null); return; }
        setAdmin1({ level: "Admin1", id: Number(v), name: nameOf(admin1List, v) });
        await load(() => fetchAdmin2ByAdmin1(token, v), setAdmin2List);
    };
    const onAdmin2 = async (v: string) => {
        setCity(null); setPostal(null);
        setCities([]); setPostalCodes([]);
        setError(null);
        if (!v) { setAdmin2(null); return; }
        setAdmin2({ level: "Admin2", id: Number(v), name: nameOf(admin2List, v) });
        await load(() => fetchCitiesByAdmin2(token, v), setCities);
    };
    const onCity = async (v: string) => {
        setPostal(null); setPostalCodes([]);
        setError(null);
        if (!v) { setCity(null); return; }
        setCity({ level: "City", id: Number(v), name: nameOf(cities, v) });
        await load(() => fetchPostalCodesByCity(token, v), setPostalCodes);
    };
    const onPostal = (v: string) => {
        if (!v) { setPostal(null); return; }
        const p = postalCodes.find((x) => String(x.id) === v);
        setPostal({ level: "PostalCode", id: Number(v), name: p ? p.postalCode : v });
    };

    // Pre-fill from geo ids (once): resolve each level's name from its list,
    // set the selection, and load the next level's options — so the picker
    // opens already pointed at the given place, still fully editable.
    const initAppliedRef = React.useRef(false);
    useEffect(() => {
        if (initAppliedRef.current || !initial?.countryId) return;
        initAppliedRef.current = true;
        (async () => {
            try {
                const nameIn = (list: IdName[], id: number) =>
                    list.find((x) => Number(x.id) === id)?.name ?? String(id);

                const cs = (await fetchCountries(token)) ?? [];
                setCountries(cs);
                setCountry({ level: "Country", id: initial.countryId!, name: nameIn(cs, initial.countryId!) });
                const a1s = (await fetchAdmin1ByCountry(token, String(initial.countryId))) ?? [];
                setAdmin1List(a1s);
                if (!initial.admin1Id) return;
                setAdmin1({ level: "Admin1", id: initial.admin1Id, name: nameIn(a1s, initial.admin1Id) });
                const a2s = (await fetchAdmin2ByAdmin1(token, String(initial.admin1Id))) ?? [];
                setAdmin2List(a2s);
                if (!initial.admin2Id) return;
                setAdmin2({ level: "Admin2", id: initial.admin2Id, name: nameIn(a2s, initial.admin2Id) });
                const cts = (await fetchCitiesByAdmin2(token, String(initial.admin2Id))) ?? [];
                setCities(cts);
                if (!initial.cityId) return;
                setCity({ level: "City", id: initial.cityId, name: nameIn(cts, initial.cityId) });
                await load(() => fetchPostalCodesByCity(token, String(initial.cityId)), setPostalCodes);
            } catch (e: any) {
                logApiError(e); // picker stays usable, just unprefixed
            }
        })();
    }, [initial, token]);

    // Applies a resolved chain (from geolocation or the remembered location):
    // sets each level and loads the next level's options, using the chain's own
    // names (state lists aren't updated yet inside this async sequence).
    const applyChain = async (chain: ReverseGeocode) => {
        if (!chain.country) return;
        setError(null);
        setAdmin1(null); setAdmin2(null); setCity(null); setPostal(null);
        setAdmin2List([]); setCities([]); setPostalCodes([]);
        setCountry({ level: "Country", id: chain.country.id, name: chain.country.name });
        await load(() => fetchAdmin1ByCountry(token, String(chain.country!.id)), setAdmin1List);
        if (!chain.admin1) return;
        setAdmin1({ level: "Admin1", id: chain.admin1.id, name: chain.admin1.name });
        await load(() => fetchAdmin2ByAdmin1(token, String(chain.admin1!.id)), setAdmin2List);
        if (!chain.admin2) return;
        setAdmin2({ level: "Admin2", id: chain.admin2.id, name: chain.admin2.name });
        await load(() => fetchCitiesByAdmin2(token, String(chain.admin2!.id)), setCities);
        if (!chain.city) return;
        setCity({ level: "City", id: chain.city.id, name: chain.city.name });
        await load(() => fetchPostalCodesByCity(token, String(chain.city!.id)), setPostalCodes);
    };

    const useMyLocation = async () => {
        setLocating(true);
        setError(null);
        try {
            const pos = await getBrowserPosition();
            const chain = await reverseGeocode(token, pos.lat, pos.lon);
            await applyChain(chain);
        } catch (e: any) {
            setError(e?.message === "denied"
                ? "Location permission was denied."
                : "Could not determine your location.");
        } finally {
            setLocating(false);
        }
    };

    // Remember the deepest selection so other pickers / next visits can reuse it.
    useEffect(() => {
        if (!country) return;
        saveLastLocation({
            country: { id: Number(country.id), name: country.name },
            admin1: admin1 ? { id: Number(admin1.id), name: admin1.name } : undefined,
            admin2: admin2 ? { id: Number(admin2.id), name: admin2.name } : undefined,
            city: city ? { id: Number(city.id), name: city.name } : undefined,
        });
    }, [country, admin1, admin2, city]);

    const sel = (v: CoverageArea | null) => (v ? String(v.id) : "");

    // Hint rendered just below the deepest-set field.
    const deepest = chain.length ? chain[chain.length - 1] : null;
    const hintAt = (level: CoverageArea["level"]) =>
        context && (deepest ? deepest.level === level : level === "Country") ? (
            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", fontStyle: "italic" }}>
                {hintFor(context, deepest)}
            </div>
        ) : null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {error && <div className="status-error">{error}</div>}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button type="button" onClick={useMyLocation} disabled={locating}>
                    {locating ? "Locating…" : "📍 Use my location"}
                </button>
                {lastLoc && deepestName(lastLoc) && (
                    <button type="button" onClick={() => applyChain(lastLoc)} disabled={locating}>
                        ↩ Last: {deepestName(lastLoc)}
                    </button>
                )}
            </div>

            <label><div>Country</div>
                <select value={sel(country)} onChange={(e) => onCountry(e.target.value)}>
                    <option value="">— select —</option>
                    {countries.map((c) => <option key={String(c.id)} value={String(c.id)}>{c.name}</option>)}
                </select>
            </label>
            {hintAt("Country")}
            {country && (
                <label><div>State / region</div>
                    <select value={sel(admin1)} onChange={(e) => onAdmin1(e.target.value)}>
                        <option value="">— any —</option>
                        {admin1List.map((a) => <option key={String(a.id)} value={String(a.id)}>{a.name}</option>)}
                    </select>
                </label>
            )}
            {hintAt("Admin1")}
            {admin1 && (
                <label><div>County</div>
                    <select value={sel(admin2)} onChange={(e) => onAdmin2(e.target.value)}>
                        <option value="">— any —</option>
                        {admin2List.map((a) => <option key={String(a.id)} value={String(a.id)}>{a.name}</option>)}
                    </select>
                </label>
            )}
            {hintAt("Admin2")}
            {admin2 && (
                <label><div>City</div>
                    <select value={sel(city)} onChange={(e) => onCity(e.target.value)}>
                        <option value="">— any —</option>
                        {cities.map((c) => <option key={String(c.id)} value={String(c.id)}>{c.name}</option>)}
                    </select>
                </label>
            )}
            {hintAt("City")}
            {city && (
                <label><div>Postal code</div>
                    <select value={sel(postal)} onChange={(e) => onPostal(e.target.value)}>
                        <option value="">— any —</option>
                        {postalCodes.map((p) => <option key={String(p.id)} value={String(p.id)}>{p.postalCode}{p.placeName ? ` (${p.placeName})` : ""}</option>)}
                    </select>
                </label>
            )}
            {hintAt("PostalCode")}
        </div>
    );
}
